import { zIndexes } from "@shared/common/Themes/AppTheme";
import React, { FC, PropsWithChildren, useEffect, useMemo } from "react";
import useMedia from "utils/hooks/useMedia";
import {
  DRAWER_VISIBILITY_CLASS,
  INTERCOM_TOTAL_SIZE,
  IS_WEB_VIEW,
  MOBILE_SEARCH,
} from "common/constants";
import AntDrawer, { DrawerProps as AntDrawerProps } from "antd/es/drawer";
import styled, { css, useTheme } from "styled-components";
import { OverlayWrapper } from "common/Overlays/Overlay";
import TelegramAppHeader from "common/Home/Header/TelegramAppHeader";
import { isMobile } from "react-device-detect";
import { Inset } from "@shared/types";
import { hexToRGB } from "@shared/utils/misc.ts";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import useBackButtonHandler from "utils/hooks/useBackButtonHandler.ts";
import { useRefValue } from "@shared/utils/hooks/misc.ts";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants.ts";
import { NEW_LOOK_PHONE_GUTTER } from "common/NewLook/layout.ts";

export interface DrawerProps extends AntDrawerProps {
  closeOnBack?: boolean;
}

const Drawer: FC<PropsWithChildren<DrawerProps>> = ({
  children,
  closeOnBack = true,
  ...props
}) => {
  const isMobileScreen = useMedia(
    [`(max-width: ${MOBILE_SEARCH})`],
    [true],
    false
  );
  const { isNewLook } = useTheme();
  const { registerBackButtonListener } = useBackButtonHandler();
  const refValues = useRefValue({ onClose: props.onClose, open: props.open });

  useEffect(() => {
    if (IS_WEB_VIEW && closeOnBack && refValues) {
      return registerBackButtonListener(() => {
        if (refValues.current.open) {
          (refValues.current.onClose as any)?.();
          return true;
        }
        return false;
      });
    }
  }, [closeOnBack]);

  const defaultProps: DrawerProps = {
    size: DEFAULT_DRAWER_WIDTH,
    placement: "right",
    destroyOnHidden: true,
    zIndex: zIndexes.drawer,
    mask: { enabled: true, blur: false },
    styles: {
      body: {
        // New look: the one phone gutter, shared with the drawer header.
        padding: isMobileScreen ? (isNewLook ? NEW_LOOK_PHONE_GUTTER : 12) : 36,
        overflow: "auto",
      },
    },
  };

  const contextValue = useMemo(
    () => ({
      isDrawer: true,
    }),
    []
  );

  return (
    <DrawerContext.Provider value={contextValue}>
      <StyledDrawer
        push={false}
        {...defaultProps}
        {...props}
        $isMobile={isMobileScreen}
        title={
          IS_TELEGRAM_WEB_APP &&
          props.placement !== "bottom" && <TelegramAppHeader />
        }
      >
        {children}
      </StyledDrawer>
    </DrawerContext.Provider>
  );
};

export default Drawer;

export const DEFAULT_DRAWER_WIDTH = 660;

export const drawerStyles = css<{
  $isMobile?: boolean;
  $safeAreaInset?: Inset;
  $contentSafeAreaInset?: Inset;
}>`
  .${DRAWER_VISIBILITY_CLASS} {
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden; /* For older webkit browsers */
    transform: translateZ(0);
  }
  .ant-drawer-content-wrapper {
    max-width: 100%;
  }
  .ant-drawer-body {
    padding-bottom: ${(p) => p.$safeAreaInset?.bottom ?? 0}px !important;
  }
  .ant-drawer-content,
  .ant-drawer-body,
  .ant-drawer-wrapper-body {
    background: ${(p) => p.theme.colors.layoutBgColor};
  }
  .ant-drawer-header {
    border-bottom: none;
    padding: 0;
  }
  .ant-drawer-close {
    padding: 12px !important;
    z-index: 40;
    color: ${(p) => p.theme.colors.textColor};
    top: ${(p) => {
      const addPadding = IS_TELEGRAM_WEB_APP && isMobile;
      const topPadding = addPadding
        ? (p.$safeAreaInset?.top ?? 0) + (p.$contentSafeAreaInset?.top ?? 0)
        : 0;
      return topPadding + 12;
    }}px;
    position: absolute;
    right: 0;
    &:hover {
      color: ${(p) => hexToRGB(p.theme.colors.textColor, 0.7)};
      background: ${(p) => hexToRGB(p.theme.colors.lightestGrey, 0.2)};
    }
  }
  ${OverlayWrapper} {
    background: ${(p) => p.theme.colors.layoutBgColor};
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      .ant-drawer-mask {
        /* A neutral dark scrim: a navy tint turns purple over the demo
           banner and blue over white. */
        background: ${hexToRGB("#0B0D12", p.theme.isDarkMode ? 0.55 : 0.32)};
      }
      .ant-drawer-close {
        border-radius: 10px;
        color: ${p.theme.colors.secondaryText};
      }
      /* Phone: a 40px hit area with a 36px visual, its glyph on the 16px
         gutter and centred on the header's 40px title row. */
      @media (max-width: ${MOBILE_SEARCH}) {
        .ant-drawer-close {
          width: ${NEW_LOOK_DRAWER_CLOSE_HIT}px;
          height: ${NEW_LOOK_DRAWER_CLOSE_HIT}px;
          padding: 0 !important;
          right: ${NEW_LOOK_DRAWER_CLOSE_RIGHT}px;
          top: ${(() => {
            const addPadding = IS_TELEGRAM_WEB_APP && isMobile;
            const topPadding = addPadding
              ? (p.$safeAreaInset?.top ?? 0) +
                (p.$contentSafeAreaInset?.top ?? 0)
              : 0;
            return topPadding + NEW_LOOK_DRAWER_HEADER_TOP;
          })()}px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          background: transparent;
          &::before {
            content: "";
            position: absolute;
            inset: 2px;
            border-radius: 10px;
            transition: background 0.15s ease;
          }
          &:hover {
            background: transparent;
          }
          &:hover::before {
            background: ${hexToRGB(p.theme.colors.lightestGrey, 0.2)};
          }
        }
      }
      /* On desktop a drawer floats as a rounded sheet inside the window. */
      @media (min-width: ${NEW_LOOK_FLOATING_DRAWER_MIN_WIDTH}px) {
        .ant-drawer-right > .ant-drawer-content-wrapper {
          top: ${NEW_LOOK_DRAWER_INSET}px;
          bottom: ${NEW_LOOK_DRAWER_INSET}px;
          right: ${NEW_LOOK_DRAWER_INSET}px;
          max-width: calc(100% - ${NEW_LOOK_DRAWER_INSET * 2}px);
          border-radius: ${p.theme.radii.card}px;
          overflow: hidden;
          box-shadow: 0 24px 64px ${hexToRGB(p.theme.colors.ink, 0.18)};
        }
      }
    `}
`;
/** New look, phone: the drawer close button's hit area (36px visual). */
export const NEW_LOOK_DRAWER_CLOSE_HIT = 40;
/** Puts the close glyph's right edge on the 16px phone gutter. */
const NEW_LOOK_DRAWER_CLOSE_RIGHT = 4;
/** New look, phone: the drawer header's top padding (title row starts here). */
export const NEW_LOOK_DRAWER_HEADER_TOP = 8;
// Below this width drawers keep covering the full height (phones, small tablets).
const NEW_LOOK_FLOATING_DRAWER_MIN_WIDTH = 900;
const NEW_LOOK_DRAWER_INSET = 8;
const StyledDrawer = styled(AntDrawer)<{ $isMobile?: boolean }>`
  .ant-drawer-body {
    padding-bottom: ${INTERCOM_TOTAL_SIZE}px !important;
  }
`;
